'use client'

import { usePathname } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { AlertTriangle, ArrowRight, BadgePercent, Check, Gift, LoaderCircle, Medal, Plus, ShoppingBag, Truck } from 'lucide-react'
import { useCart, useCartRecommendations } from '@/lib/commerce/hooks'
import { formatImageURL, formatPrice, getCheckoutPath, getBuyXTriggerCount, getBuyXGetYRewardRule } from '@/utils/util'
import { navigateUrl } from '@/utils/client-request'
import { Skeleton } from '@/components/ui/skeleton'
import CartCard from './CartCard'
import ProductCard from '../product/ProductCard'

export default function Cart({ vendor }: { vendor: any }) {
  const pathname = usePathname() || ''
  const { data: session } = useSession()
  const isAuthed = !!session?.user?.id
  const { cart, cartCount, initialLoading, isValidating, canCheckout, amount, sale, orderDiscountAmount, couponDiscount, cartTotal, freeShippingProgress, hasFreeShipping, buyXGetYInfo, addRewardItem, availableOrderRewardItems, availableCouponRewardItems, rewardLoading, activeOrderDiscount, appliedCoupon, getRewardPrice, isRewardOutOfStock, getBuyXGetYRewardState, loyaltyDiscountAmount, loyaltySaleOffsetAmount } = useCart()
  const cartItems: any[] = cart?.products || []
  const { recommendedProducts, isLoading: isLoadingRecommendations } = useCartRecommendations(vendor, cartItems, 5)
  const subtotal = amount - sale
  const validCoupon = couponDiscount > 0 && couponDiscount <= subtotal ? couponDiscount : 0
  const discounts = (orderDiscountAmount || 0) + validCoupon
  const loyaltyDiscount = loyaltyDiscountAmount > 0 ? Math.min(loyaltyDiscountAmount, Math.max(0, cartTotal - (orderDiscountAmount || 0) - validCoupon)) : 0
  const displaySubtotal = subtotal + loyaltySaleOffsetAmount
  const total = Math.max(0, cartTotal - (orderDiscountAmount || 0) - validCoupon - loyaltyDiscount)

  const shippingTarget = freeShippingProgress?.minOrderAmount || 0
  const shippingPct = freeShippingProgress?.percentage || 0
  const shippingRemaining = freeShippingProgress?.remaining || 0
  const belowMin = vendor?.orderMinAmount > 0 && subtotal < vendor.orderMinAmount

  return (
    <section className="bg-cream pt-6 pb-16 min-h-screen">
      <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10">
        <nav className="text-[13px] text-foreground/55 mb-6">
          <a href={navigateUrl('/', pathname)} className="hover:text-foreground">Нүүр</a>
          <span className="mx-1.5">/</span>
          <span className="text-foreground">Сагс</span>
        </nav>

        {initialLoading ? (
          <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-8">
            <div>
              <div className="flex items-end justify-between mb-6 gap-4">
                <Skeleton className="h-12 sm:h-16 w-72 rounded-2xl" />
                <Skeleton className="h-4 w-20" />
              </div>
              <div className="bg-white rounded-3xl p-5 mb-5 flex items-center gap-4">
                <Skeleton className="w-11 h-11 rounded-full shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-1.5 w-full rounded-full" />
                </div>
              </div>
              <div className="bg-white rounded-3xl px-6">
                {[0, 1, 2].map(i => (
                  <div key={i} className="grid grid-cols-[88px_1fr_auto] gap-4.5 py-5 border-b border-foreground/10 last:border-0 items-center">
                    <Skeleton className="w-22 h-22 rounded-2xl" />
                    <div className="space-y-2">
                      <Skeleton className="h-3 w-20" />
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-1/3" />
                      <Skeleton className="h-9 w-32 rounded-full mt-2" />
                    </div>
                    <div className="space-y-2 text-right">
                      <Skeleton className="h-6 w-24 ml-auto" />
                      <Skeleton className="h-3 w-16 ml-auto" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <aside>
              <div className="bg-white rounded-3xl p-7 flex flex-col gap-5">
                <div className="space-y-2">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-7 w-32" />
                </div>
                <Skeleton className="h-11 w-full rounded-full" />
                <div className="space-y-2.5">
                  <div className="flex justify-between"><Skeleton className="h-4 w-24" /><Skeleton className="h-4 w-20" /></div>
                  <div className="flex justify-between"><Skeleton className="h-4 w-16" /><Skeleton className="h-4 w-20" /></div>
                  <div className="flex justify-between"><Skeleton className="h-4 w-20" /><Skeleton className="h-4 w-20" /></div>
                </div>
                <div className="pt-4 border-t border-foreground/10 flex items-baseline justify-between">
                  <Skeleton className="h-5 w-14" />
                  <Skeleton className="h-9 w-32" />
                </div>
                <Skeleton className="h-14 w-full rounded-full" />
              </div>
            </aside>
          </div>
        ) : cartCount === 0 ? (
          <div className="py-20 flex flex-col items-center text-center">
            <div className="w-24 h-24 rounded-3xl bg-[#F1EEE7] inline-flex items-center justify-center mb-5">
              <ShoppingBag className="size-10 text-foreground/40" />
            </div>
            <h1 className="text-[28px] font-bold font-gilroy tracking-[-0.02em] text-foreground">Таны сагс хоосон байна</h1>
            <p className="mt-2 text-[15px] text-foreground/55 max-w-md">Дэлгүүрээс таалагдсан бараагаа сонгоод, сагсандаа нэмээрэй.</p>
            <a href={navigateUrl('/product', pathname)} className="mt-6 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-foreground text-cream text-[14px] font-semibold hover:bg-foreground/90 transition-colors">
              <ShoppingBag className="size-4" /> Дэлгүүр хэсэх
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-8">
            <div>
              <div className="flex items-end justify-between mb-6 gap-4">
                <h1 className="font-bold font-gilroy tracking-[-0.035em] leading-[0.95] text-foreground" style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)' }}>
                  Таны сагс.
                </h1>
                <div className="pb-2 text-[14px] text-foreground/55 shrink-0">{cartCount} ширхэг</div>
              </div>

              {shippingTarget > 0 && !hasFreeShipping && (
                <div className="bg-white rounded-3xl p-5 mb-5 flex items-center gap-4">
                  <div className="w-11 h-11 rounded-full bg-[#E8E2FF] text-foreground inline-flex items-center justify-center shrink-0">
                    <Truck className="size-4.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-semibold text-foreground mb-2">
                      <span className="text-foreground">{formatPrice(shippingRemaining)}</span> хүрвэл үнэгүй хүргэлт. Бараг боллоо!
                    </div>
                    <div className="h-1.5 rounded-full bg-foreground/10 overflow-hidden">
                      <div className="h-full bg-foreground transition-all" style={{ width: `${shippingPct}%` }} />
                    </div>
                  </div>
                </div>
              )}
              {hasFreeShipping && (
                <div className="bg-emerald-50 border border-emerald-100 rounded-3xl px-5 py-4 mb-5 flex items-center gap-3 text-[14px] font-semibold text-emerald-700">
                  <Truck className="size-4.5" /> Үнэгүй хүргэлт идэвхтэй болсон
                </div>
              )}

              <div className="bg-white rounded-3xl px-6">
                {cartItems.map((p: any) => (
                  <CartCard key={`${p.isRewardItem ? 'reward-' : ''}${p.id}-${p.variant?.id}`} product={p} />
                ))}
              </div>

              {buyXGetYInfo?.map((info: any) => info.qualifies && info.rewardProducts?.length > 0 && (() => {
                const { totalRemaining } = getBuyXGetYRewardState(info, info.rewardProducts[0])
                if (totalRemaining <= 0) return null
                return (
                  <div key={info.discount.id} className="mt-5 rounded-3xl bg-white border border-foreground/10 p-5">
                    <div className="flex items-center gap-3">
                      <span className="w-10 h-10 rounded-full bg-[#F1EEE7] text-foreground inline-flex items-center justify-center shrink-0">
                        <Gift className="size-4.5" strokeWidth={2.25} />
                      </span>
                      <div className="min-w-0">
                        <div className="text-[14px] font-semibold text-foreground leading-tight">{info.discount?.name || 'Урамшуулал'}</div>
                        <div className="text-[12px] text-foreground/55 mt-0.5">
                          {getBuyXTriggerCount(info.discount)} авбал {getBuyXGetYRewardRule(info)}
                        </div>
                      </div>
                    </div>
                    <div className="mt-2.5 inline-flex items-center gap-1.5 text-[12px] text-emerald-700 font-semibold">
                      <Check className="size-3.5" /> Нөхцөл хангагдлаа · {info.triggerCount}/{getBuyXTriggerCount(info.discount)}
                      {totalRemaining > 0 ? ` · ${totalRemaining} бэлэг авах боломжтой` : ''}
                    </div>
                    <div className="mt-3 space-y-2">
                      {info.rewardProducts.map((reward: any) => {
                        const { canAdd, isLockedByOther, isOutOfStock, rewardPrice } = getBuyXGetYRewardState(info, reward)
                        if (isLockedByOther) return null
                        return (
                          <div key={reward.id} className="flex items-center gap-3 p-3 bg-cream rounded-2xl">
                            <div className="w-12 h-12 shrink-0 rounded-xl overflow-hidden bg-[#F1EEE7]">
                              <img src={formatImageURL(reward.image || 'placeholder.png')} alt={reward.name} className="w-full h-full object-cover" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <div className="text-[13.5px] font-semibold text-foreground line-clamp-1">{reward.name}</div>
                                {reward.valueType === 'free' ? (
                                  <span className="bg-red-500 text-white px-1.5 h-[18px] rounded-full text-[10px] font-bold inline-flex items-center shrink-0">Үнэгүй</span>
                                ) : reward.valueType === 'percent' && reward.valuePercent ? (
                                  <span className="bg-red-500 text-white px-1.5 h-[18px] rounded-full text-[10px] font-bold inline-flex items-center shrink-0">-{reward.valuePercent}%</span>
                                ) : null}
                              </div>
                              <div className="mt-0.5 flex items-center gap-1.5">
                                <span className={`text-[12.5px] font-semibold tabular-nums ${rewardPrice === 0 ? 'text-emerald-600' : 'text-foreground'}`}>{rewardPrice === 0 ? 'Үнэгүй' : formatPrice(rewardPrice)}</span>
                                {reward.price && rewardPrice < reward.price && <span className="text-[11.5px] text-foreground/40 line-through tabular-nums">{formatPrice(reward.price)}</span>}
                              </div>
                            </div>
                            <button
                              type="button"
                              disabled={!canAdd || rewardLoading}
                              onClick={() => addRewardItem({ ...reward, discountId: info.discount.id, discountName: info.discount.name, discountBase: info.discount.discountBase, variant: reward.variant || { id: reward.defaultVariantId, price: reward.price, priceSale: reward.priceSale, stock: reward.stock } }, 'buyXgetY')}
                              className="h-9 px-3.5 rounded-full bg-foreground text-cream text-[12.5px] font-semibold inline-flex items-center gap-1.5 hover:bg-foreground/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                            >
                              {rewardLoading ? <LoaderCircle className="size-3.5 animate-spin" /> : canAdd ? <><Plus className="size-3.5" /> Нэмэх</> : isOutOfStock ? 'Дууссан' : 'Нэмэгдсэн'}
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })())}

              {availableCouponRewardItems?.length > 0 && (
                <div className="mt-5 rounded-3xl bg-white border border-foreground/10 p-5">
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-full bg-[#F1EEE7] text-foreground inline-flex items-center justify-center shrink-0">
                      <Gift className="size-4.5" strokeWidth={2.25} />
                    </span>
                    <div className="min-w-0">
                      <div className="text-[14px] font-semibold text-foreground leading-tight">{appliedCoupon?.discount?.name || appliedCoupon?.name || 'Купоны бэлэг'}</div>
                      <div className="text-[12px] text-foreground/55 mt-0.5">Хөнгөлөлтийн кодын бэлэг сонгоно уу</div>
                    </div>
                  </div>
                  <div className="mt-3 space-y-2">
                    {availableCouponRewardItems.map((reward: any) => {
                      const isOutOfStock = isRewardOutOfStock(reward)
                      const rewardPrice = getRewardPrice(reward)
                      return (
                        <div key={reward.id} className="flex items-center gap-3 p-3 bg-cream rounded-2xl">
                          <div className="w-12 h-12 shrink-0 rounded-xl overflow-hidden bg-white">
                            <img src={formatImageURL(reward.image || 'placeholder.png')} alt={reward.name} className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <div className="text-[13.5px] font-semibold text-foreground line-clamp-1">{reward.name}</div>
                              {reward.valueType === 'free' ? (
                                <span className="bg-red-500 text-white px-1.5 h-[18px] rounded-full text-[10px] font-bold inline-flex items-center shrink-0">Үнэгүй</span>
                              ) : reward.valueType === 'percent' && reward.valuePercent ? (
                                <span className="bg-red-500 text-white px-1.5 h-[18px] rounded-full text-[10px] font-bold inline-flex items-center shrink-0">-{reward.valuePercent}%</span>
                              ) : reward.valueType === 'amount' && reward.valueAmount ? (
                                <span className="bg-red-500 text-white px-1.5 h-[18px] rounded-full text-[10px] font-bold inline-flex items-center shrink-0">-{formatPrice(reward.valueAmount)}</span>
                              ) : null}
                            </div>
                            <div className="mt-0.5 flex items-center gap-1.5">
                              <span className={`text-[12.5px] font-semibold tabular-nums ${rewardPrice === 0 ? 'text-emerald-600' : 'text-foreground'}`}>{rewardPrice === 0 ? 'Үнэгүй' : formatPrice(rewardPrice)}</span>
                              {reward.price && rewardPrice < reward.price && <span className="text-[11.5px] text-foreground/40 line-through tabular-nums">{formatPrice(reward.price)}</span>}
                            </div>
                          </div>
                          <button
                            type="button"
                            disabled={isOutOfStock || rewardLoading}
                            onClick={() => addRewardItem(reward, 'order')}
                            className="h-9 px-3.5 rounded-full bg-foreground text-cream text-[12.5px] font-semibold inline-flex items-center gap-1.5 hover:bg-foreground/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                          >
                            {rewardLoading ? <LoaderCircle className="size-3.5 animate-spin" /> : isOutOfStock ? 'Дууссан' : <><Plus className="size-3.5" /> Нэмэх</>}
                          </button>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {availableOrderRewardItems?.length > 0 && (
                <div className="mt-5 rounded-3xl bg-white border border-foreground/10 p-5">
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-full bg-[#F1EEE7] text-foreground inline-flex items-center justify-center shrink-0">
                      <Gift className="size-4.5" strokeWidth={2.25} />
                    </span>
                    <div className="min-w-0">
                      <div className="text-[14px] font-semibold text-foreground leading-tight">Захиалгын бэлэг</div>
                      <div className="text-[12px] text-foreground/55 mt-0.5">Захиалгын урамшууллын бэлэг сонгоно уу</div>
                    </div>
                  </div>
                  <div className="mt-3 space-y-2">
                    {availableOrderRewardItems.map((reward: any) => {
                      const isOutOfStock = isRewardOutOfStock(reward)
                      const rewardPrice = getRewardPrice(reward)
                      return (
                        <div key={reward.id} className="flex items-center gap-3 p-3 bg-white rounded-2xl">
                          <div className="w-12 h-12 shrink-0 rounded-xl overflow-hidden bg-[#F1EEE7]">
                            <img src={formatImageURL(reward.image || 'placeholder.png')} alt={reward.name} className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <div className="text-[13.5px] font-semibold text-foreground line-clamp-1">{reward.name}</div>
                              {reward.valueType === 'free' ? (
                                <span className="bg-red-500 text-white px-1.5 h-[18px] rounded-full text-[10px] font-bold inline-flex items-center shrink-0">Үнэгүй</span>
                              ) : reward.valueType === 'percent' && reward.valuePercent ? (
                                <span className="bg-red-500 text-white px-1.5 h-[18px] rounded-full text-[10px] font-bold inline-flex items-center shrink-0">-{reward.valuePercent}%</span>
                              ) : null}
                            </div>
                            <div className="mt-0.5 flex items-center gap-1.5">
                              <span className={`text-[12.5px] font-semibold tabular-nums ${rewardPrice === 0 ? 'text-emerald-600' : 'text-foreground'}`}>{rewardPrice === 0 ? 'Үнэгүй' : formatPrice(rewardPrice)}</span>
                              {reward.price && rewardPrice < reward.price && <span className="text-[11.5px] text-foreground/40 line-through tabular-nums">{formatPrice(reward.price)}</span>}
                            </div>
                          </div>
                          <button
                            type="button"
                            disabled={isOutOfStock || rewardLoading}
                            onClick={() => addRewardItem(reward, 'order')}
                            className="h-9 px-3.5 rounded-full bg-foreground text-cream text-[12.5px] font-semibold inline-flex items-center gap-1.5 hover:bg-foreground/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                          >
                            {rewardLoading ? <LoaderCircle className="size-3.5 animate-spin" /> : isOutOfStock ? 'Дууссан' : <><Plus className="size-3.5" /> Нэмэх</>}
                          </button>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>

            <aside className="lg:sticky lg:top-24 lg:self-start">
              <div className="bg-white rounded-3xl p-7 flex flex-col gap-5">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground/55 mb-1.5">Захиалга</div>
                  <div className="text-[22px] font-bold font-gilroy tracking-[-0.015em] text-foreground">Хураангуй</div>
                </div>

                {activeOrderDiscount?.type === 'order' && !appliedCoupon && activeOrderDiscount.minOrderAmount > 0 && (() => {
                  const minAmt = activeOrderDiscount.minOrderAmount
                  const target = activeOrderDiscount.targets?.find((t: any) => t.role === 'target' && t.targetId === null)
                  if (!target) return null
                  const discountLabel = target.valueType === 'percent' ? `${target.valuePercent}%-ийн хөнгөлөлт` : target.valueType === 'amount' ? `${formatPrice(target.valueAmount)} хөнгөлөлт` : null
                  if (!discountLabel) return null
                  const reached = subtotal >= minAmt
                  return (
                    <div className="rounded-2xl bg-purple-50 border border-purple-100 px-4 py-3">
                      <div className="flex items-start gap-2 text-[13px] text-purple-900">
                        <BadgePercent className="size-4 mt-0.5 shrink-0" />
                        <span className="font-medium">{formatPrice(minAmt)}-с дээш захиалгад {discountLabel}</span>
                      </div>
                      <div className="mt-2 flex items-center gap-3">
                        <div className="flex-1 h-1.5 rounded-full bg-purple-200 overflow-hidden">
                          <div className="h-full bg-purple-600 transition-all" style={{ width: `${Math.min((subtotal / minAmt) * 100, 100)}%` }} />
                        </div>
                        {reached ? (
                          <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-purple-700 shrink-0">
                            <Check className="size-3.5" /> −{formatPrice(orderDiscountAmount)}
                          </span>
                        ) : (
                          <span className="text-[12px] font-semibold text-purple-700 tabular-nums shrink-0">{formatPrice(minAmt - subtotal)} дутуу</span>
                        )}
                      </div>
                    </div>
                  )
                })()}

                {belowMin && (
                  <div className="rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 flex items-start gap-3">
                    <AlertTriangle className="size-4 text-amber-600 shrink-0 mt-0.5" />
                    <div className="text-[12.5px] leading-snug">
                      <div className="font-semibold text-amber-800">Захиалгын доод дүнд хүрээгүй</div>
                      <div className="text-amber-700 mt-0.5">Хамгийн багадаа <span className="font-semibold tabular-nums">{formatPrice(vendor.orderMinAmount)}</span>-ийн захиалга өгөх ёстой. <span className="font-semibold tabular-nums">{formatPrice(vendor.orderMinAmount - subtotal)}</span> дутуу.</div>
                      <div className="mt-2 h-1.5 rounded-full bg-amber-200 overflow-hidden">
                        <div className="h-full bg-amber-500 transition-all" style={{ width: `${Math.min(100, (subtotal / vendor.orderMinAmount) * 100)}%` }} />
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-2 text-[14px]">
                  <div className="flex items-center justify-between">
                    <span className="text-foreground/60">Барааны үнэ ({cartCount} бараа)</span>
                    <span className="text-foreground font-medium">{formatPrice(displaySubtotal)}</span>
                  </div>
                  {discounts > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-foreground/60">Хямдрал</span>
                      <span className="text-emerald-600 font-medium">−{formatPrice(discounts)}</span>
                    </div>
                  )}
                  {loyaltyDiscount > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center gap-1.5 text-violet-600 font-medium"><Medal className="size-4" /> Лояалти хөнгөлөлт</span>
                      <span className="text-violet-600 font-medium tabular-nums">−{formatPrice(loyaltyDiscount + loyaltySaleOffsetAmount)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-foreground/60">Хүргэлт</span>
                    <span className="text-foreground font-medium">{hasFreeShipping ? 'Үнэгүй' : 'Захиалгад тооцно'}</span>
                  </div>
                </div>

                <div className="pt-4 border-t border-foreground/10 flex items-baseline justify-between">
                  <span className="text-[15px] font-semibold text-foreground">Нийт</span>
                  <span className="text-[36px] font-bold font-gilroy tracking-[-0.025em] text-foreground">{formatPrice(total)}</span>
                </div>

                {belowMin || isValidating || !canCheckout() ? (
                  <button
                    type="button"
                    disabled
                    className="w-full h-14 rounded-full bg-foreground/40 text-cream/80 text-[15px] font-semibold inline-flex items-center justify-center gap-2.5 cursor-not-allowed"
                  >
                    Худалдан авах <ArrowRight className="size-4" />
                  </button>
                ) : (
                  <a
                    href={navigateUrl(getCheckoutPath(vendor, isAuthed), pathname)}
                    className="w-full h-14 rounded-full bg-foreground text-cream text-[15px] font-semibold inline-flex items-center justify-center gap-2.5 hover:bg-foreground/90 transition-colors"
                  >
                    Худалдан авах <ArrowRight className="size-4" />
                  </a>
                )}
              </div>
            </aside>
          </div>
        )}

        {cartCount > 0 && (isLoadingRecommendations || recommendedProducts.length > 0) && (
          <div className="mt-16 sm:mt-20">
            <div className="flex items-end justify-between mb-6 gap-4">
              <h2 className="font-bold font-gilroy tracking-[-0.025em] leading-none text-foreground" style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)' }}>
                Танд таалагдаж магадгүй
              </h2>
            </div>
            {isLoadingRecommendations ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-5 gap-y-8">
                {[...Array(5)].map((_, i) => (
                  <div key={i}>
                    <Skeleton className="aspect-square w-full rounded-2xl" />
                    <Skeleton className="h-4 w-3/4 mt-4" />
                    <Skeleton className="h-5 w-1/3 mt-2" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-5 gap-y-8">
                {recommendedProducts.map((product: any) => (
                  <ProductCard key={product.customId || product.id} product={product} vendor={vendor} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  )
}
